import { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { symbolSpecWarnings, type SymbolSpecField, type SymbolSpecWarning } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { Button, ErrorNote, Field, SidePanel, inputClass, useCanWrite } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { api, errorMessage, type SymbolInput } from "@/lib/api";
import { usePropFirms, useRefreshDirectory, useServers } from "@/lib/queries";
import { decimalInput, intInput } from "@/lib/units";
import type { BrokerServer, SymbolCandidates, SymbolMapping } from "@/lib/types";

/** Decimal dal DB senza zeri di coda: "0.01000000" -> "0.01". */
const trim = (d: string) => String(Number(d));

export function Servers() {
  const canWrite = useCanWrite();
  const servers = useServers();
  const [params] = useSearchParams();
  const focusId = params.get("server");
  const [serverPanel, setServerPanel] = useState(false);
  const [symbolPanel, setSymbolPanel] = useState<{ server: BrokerServer; symbol: SymbolMapping | null; canonical?: string } | null>(null);

  /**
   * Il valore punto dello stesso simbolo sugli ALTRI server. È il confronto che
   * fa notare un numero fuori scala senza dover sapere niente del simbolo: se un
   * server dice cento volte meno di tutti gli altri, uno dei due sbaglia.
   */
  const peersOf = (serverId: string, canonical: string) =>
    (servers.data ?? [])
      .filter((s) => s.id !== serverId)
      .flatMap((s) => s.symbols.filter((m) => m.canonical === canonical).map((m) => m.valuePerPoint));

  return (
    <>
      <PageHeader
        title="Server"
        subtitle="Server MT5 e mappatura dei simboli: le specifiche da cui il motore calcola lotti e rischio"
        actions={canWrite && <Button variant="primary" onClick={() => setServerPanel(true)}>Nuovo server</Button>}
      />

      {servers.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : servers.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(servers.error)}</ErrorNote>
        </div>
      ) : !servers.data?.length ? (
        <EmptyState
          title="Nessun server"
          hint="Aggiungi il server del conto prop e quello del broker, poi mappa su entrambi il simbolo che userai: senza mappatura le fasi non possono calcolare i lotti."
          action={canWrite && <Button variant="primary" onClick={() => setServerPanel(true)}>Nuovo server</Button>}
        />
      ) : (
        <div className="space-y-4 px-6 py-4">
          {servers.data.map((srv) => (
            <ServerCard
              key={srv.id}
              server={srv}
              focus={srv.id === focusId}
              canWrite={canWrite}
              peersOf={peersOf}
              onAdd={(canonical) => setSymbolPanel({ server: srv, symbol: null, canonical })}
              onEdit={(symbol) => setSymbolPanel({ server: srv, symbol })}
            />
          ))}
        </div>
      )}

      <ServerPanel open={serverPanel} onClose={() => setServerPanel(false)} />
      {symbolPanel && (
        <SymbolPanel
          server={symbolPanel.server}
          symbol={symbolPanel.symbol}
          canonical={symbolPanel.canonical}
          peersOf={peersOf}
          onClose={() => setSymbolPanel(null)}
        />
      )}
    </>
  );
}

function ServerCard({
  server,
  focus,
  canWrite,
  peersOf,
  onAdd,
  onEdit,
}: {
  server: BrokerServer;
  focus: boolean;
  canWrite: boolean;
  peersOf: (serverId: string, canonical: string) => string[];
  onAdd: (canonical?: string) => void;
  onEdit: (s: SymbolMapping) => void;
}) {
  const ref = useRef<HTMLElement>(null);
  const [params] = useSearchParams();
  const wanted = params.get("symbol");

  useEffect(() => {
    if (focus) ref.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [focus]);

  const isProp = server.kind === "PROP";
  const missingWanted = focus && wanted && !server.symbols.some((s) => s.canonical === wanted);

  return (
    <section
      ref={ref}
      className={`card overflow-hidden ${isProp ? "pair-side-prop" : "pair-side-broker"} ${focus ? "ring-1 ring-muted" : ""}`}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className={`text-xs font-medium ${isProp ? "text-prop" : "text-broker"}`}>{isProp ? "Prop" : "Broker"}</span>
          <h2 className="text-sm font-semibold text-ink">{server.displayName ?? server.name}</h2>
          {server.propFirm && <span className="text-sm text-muted">{server.propFirm.name}</span>}
          <span className="text-sm text-faint">{server.timezone}</span>
          <span className="text-sm text-faint">
            {server._count.accounts} {server._count.accounts === 1 ? "conto" : "conti"}
          </span>
        </div>
        {canWrite && (
          <Button size="sm" onClick={() => onAdd(missingWanted ? wanted : undefined)}>
            Aggiungi simbolo
          </Button>
        )}
      </header>

      {missingWanted && (
        <p className="border-b border-line bg-warn/10 px-4 py-2 text-sm text-warn">
          {wanted} non è mappato su questo server: finché manca, la fase non può usarlo.
        </p>
      )}

      {server.symbols.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted">
          Nessun simbolo mappato. Aggiungi quello su cui opererai, con le specifiche lette dal terminale: senza, le fasi su questo server non calcolano i lotti.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="px-4 py-2 font-medium">Canonico</th>
                <th className="py-2 pr-4 font-medium">Simbolo sul server</th>
                <th className="py-2 pr-4 text-right font-medium">Digits</th>
                <th className="py-2 pr-4 text-right font-medium">Point</th>
                <th className="py-2 pr-4 text-right font-medium">Valore punto</th>
                <th className="py-2 pr-4 text-right font-medium">Lot step</th>
                <th className="py-2 pr-4 text-right font-medium">Lotto minimo</th>
                <th className="py-2 pr-4 text-right font-medium">Stops level</th>
                <th className="py-2 pr-4">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {server.symbols.map((m) => {
                const avvisi = symbolSpecWarnings(m, { serverKind: server.kind, peers: peersOf(server.id, m.canonical) });
                // Un numero fuori scala non si vede se ha la grafica degli altri.
                const cell = (field: SymbolSpecField) => {
                  const w = avvisi.filter((a) => a.field === field);
                  return w.length
                    ? { className: "py-2 pr-4 text-right font-medium text-warn underline decoration-warn decoration-dotted underline-offset-4", title: w.map((a) => a.message).join(" ") }
                    : { className: "py-2 pr-4 text-right" };
                };
                return (
                  <Fragment key={m.id}>
                    <tr className={`border-b border-line/60 ${avvisi.length ? "" : "last:border-0"}`}>
                      <td className="px-4 py-2 font-medium text-ink">{m.canonical}</td>
                      <td className="py-2 pr-4 text-ink">
                        <span className="font-mono">{m.brokerSymbol}</span>
                        {/* Da dove vengono i numeri: letti dal broker o scritti a mano. */}
                        <span className={`ml-2 text-xs ${m.syncedAt ? "text-muted" : "text-warn"}`} title={m.syncedAt ? `Letti dal broker il ${new Date(m.syncedAt).toLocaleDateString("it-IT")}` : "Scritti a mano: mai confrontati col broker"}>
                          {m.syncedAt ? "dal broker" : "a mano"}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">{m.digits}</td>
                      <td {...cell("point")}>{trim(m.point)}</td>
                      <td {...cell("valuePerPoint")}>{trim(m.valuePerPoint)}</td>
                      <td {...cell("lotStep")}>{trim(m.lotStep)}</td>
                      <td {...cell("minLot")}>{trim(m.minLot)}</td>
                      <td className="py-2 pr-4 text-right">{m.stopsLevel}</td>
                      <td className="py-2 pr-4 text-right">
                        {canWrite && (
                          <span className="inline-flex flex-wrap items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => onEdit(m)}>
                              Modifica
                            </Button>
                            {/* `XAUUSD.m` scelto al posto di `XAUUSD` si cancella: le
                                operazioni gia' registrate tengono il nome e restano
                                leggibili, e l'anteprima lo dice col numero. */}
                            <DeleteForever kind="simbolo" id={m.id} serverId={server.id} label={`${m.canonical} → ${m.brokerSymbol}`} size="sm" />
                          </span>
                        )}
                      </td>
                    </tr>
                    {avvisi.length > 0 && (
                      <tr className="border-b border-line/60 last:border-0">
                        <td colSpan={9} className="px-4 pb-2 text-xs text-warn">
                          {avvisi.map((a) => (
                            <span key={a.code} className="block">
                              {a.message}
                            </span>
                          ))}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * La mappatura di un simbolo (DECISIONE 27).
 *
 * La strada normale e' **rilevare**: il server sa quali simboli ha, e i numeri
 * che contano li sa lui, non noi. L'operatore sceglie il nome fra quelli veri e
 * i parametri li scrive il backend rileggendoli dal gateway.
 *
 * La scrittura a mano resta come ripiego, perche' senza un conto collegato su
 * quel server non c'e' niente da rilevare — ma i valori scritti a mano restano
 * NON verificati, e si vede.
 */
function SymbolPanel({
  server,
  symbol,
  canonical: presetCanonical,
  peersOf,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  peersOf: (serverId: string, canonical: string) => string[];
  onClose: () => void;
}) {
  const [manuale, setManuale] = useState(false);
  if (manuale) {
    return <SymbolManualForm server={server} symbol={symbol} canonical={presetCanonical} peersOf={peersOf} onClose={onClose} />;
  }
  return <SymbolDetect server={server} symbol={symbol} canonical={presetCanonical} onManuale={() => setManuale(true)} onClose={onClose} />;
}

function SymbolDetect({
  server,
  symbol,
  canonical: presetCanonical,
  onManuale,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  onManuale: () => void;
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [canonical, setCanonical] = useState(symbol?.canonical ?? presetCanonical ?? "");
  const [found, setFound] = useState<SymbolCandidates | null>(null);
  /** Nessuna preselezione, nemmeno con un candidato solo: e' il punto. */
  const [scelto, setScelto] = useState<string | null>(null);
  const [busy, setBusy] = useState<"rileva" | "salva" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [senzaConto, setSenzaConto] = useState(false);

  const rileva = async () => {
    const c = canonical.trim().toUpperCase();
    if (!c) return setError("Scrivi il simbolo canonico, per esempio XAUUSD.");
    setError(null);
    setSenzaConto(false);
    setFound(null);
    setScelto(null);
    setBusy("rileva");
    try {
      setFound(await api.servers.candidates(server.id, c));
    } catch (err) {
      const msg = errorMessage(err);
      setSenzaConto(/conto collegato/i.test(msg));
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  const salva = async () => {
    if (!scelto) return;
    setError(null);
    setBusy("salva");
    try {
      await api.servers.syncSymbol(server.id, { canonical: canonical.trim().toUpperCase(), brokerSymbol: scelto });
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const num = (x: number) => String(Number(x));

  return (
    <SidePanel
      open
      onClose={onClose}
      title={symbol ? `Rileva ${symbol.canonical}` : "Mappa un simbolo"}
      subtitle={`${server.displayName ?? server.name} · i nomi e i numeri li legge il broker, la corrispondenza la scegli tu`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" onClick={salva} disabled={!scelto || busy !== null}>
            {busy === "salva" ? "Salvataggio…" : scelto ? `Usa ${scelto}` : "Scegli un simbolo"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Simbolo canonico" hint="Il nome nostro, uguale su tutti i server. Per esempio XAUUSD.">
              {(id) => (
                <input
                  id={id}
                  value={canonical}
                  onChange={(e) => setCanonical(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), rileva())}
                  className={inputClass}
                />
              )}
            </Field>
          </div>
          <Button onClick={rileva} disabled={busy !== null}>
            {busy === "rileva" ? "Lettura…" : "Rileva dal broker"}
          </Button>
        </div>

        {senzaConto ? (
          <div role="status" className="space-y-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            <p>{error}</p>
            <p className="text-xs text-warn/80">
              I simboli si leggono da una sessione aperta: senza un conto collegato su questo server non c'è niente da
              rilevare.
            </p>
            <Button onClick={onManuale}>Scrivi a mano (non verificato)</Button>
          </div>
        ) : (
          <ErrorNote>{error}</ErrorNote>
        )}

        {found && (
          <>
            <p className="text-xs text-faint">
              {found.candidates.length === 0
                ? `Nessuno dei ${found.symbolsOnServer} simboli di questo server somiglia a ${canonical.trim().toUpperCase()}.`
                : `${found.candidates.length} su ${found.symbolsOnServer} simboli del server, letti da ${found.readFrom.label}. Nessuno è preselezionato: guarda il contract size prima di scegliere.`}
            </p>
            <div className="space-y-2">
              {found.candidates.map((c) => {
                const attivo = scelto === c.brokerSymbol;
                return (
                  <button
                    key={c.brokerSymbol}
                    type="button"
                    onClick={() => setScelto(c.brokerSymbol)}
                    className={`w-full rounded border px-3 py-2 text-left text-sm transition ${
                      attivo ? "border-accent bg-accent/10" : "border-line hover:border-accent/50"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-mono font-medium text-ink">{c.brokerSymbol}</span>
                      <span className="text-xs text-faint">{c.profitCurrency}</span>
                    </div>
                    <dl className="mt-1 grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-muted tabular-nums">
                      <div>
                        <dt className="inline text-faint">contract size </dt>
                        <dd className="inline text-ink">{num(c.contractSize)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">valore punto </dt>
                        <dd className="inline text-ink">{num(c.valuePerPoint)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">lot step </dt>
                        <dd className="inline text-ink">{num(c.lotStep)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">lotti </dt>
                        <dd className="inline text-ink">
                          {num(c.minLot)}–{num(c.maxLot)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">digits </dt>
                        <dd className="inline text-ink">{c.digits}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">stops level </dt>
                        <dd className="inline text-ink">{c.stopsLevel}</dd>
                      </div>
                    </dl>
                    {c.blocker && <p className="mt-1 text-xs text-warn">Si salva, ma resta non verificato: {c.blocker}.</p>}
                    {c.warnings.map((w) => (
                      <p key={w.code} className="mt-1 text-xs text-warn">
                        {w.message}
                      </p>
                    ))}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={onManuale} className="text-xs text-faint underline">
              Nessuno di questi: scrivo a mano (non verificato)
            </button>
          </>
        )}
      </div>
    </SidePanel>
  );
}

function SymbolManualForm({
  server,
  symbol,
  canonical: presetCanonical,
  peersOf,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  peersOf: (serverId: string, canonical: string) => string[];
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [canonical, setCanonical] = useState(symbol?.canonical ?? presetCanonical ?? "");
  const [brokerSymbol, setBrokerSymbol] = useState(symbol?.brokerSymbol ?? presetCanonical ?? "");
  const [digits, setDigits] = useState(symbol ? String(symbol.digits) : "");
  const [point, setPoint] = useState(symbol ? trim(symbol.point) : "");
  // Nessun valore predefinito: 1.0 vale su XAUUSD e non altrove.
  const [valuePerPoint, setValuePerPoint] = useState(symbol ? trim(symbol.valuePerPoint) : "");
  const [lotStep, setLotStep] = useState(symbol ? trim(symbol.lotStep) : "");
  const [minLot, setMinLot] = useState(symbol ? trim(symbol.minLot) : "");
  const [stopsLevel, setStopsLevel] = useState(symbol ? String(symbol.stopsLevel) : "0");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Gli avvisi si vedono mentre si scrive, PRIMA di salvare, e non bloccano: un
   * numero strano può essere vero (i broker esotici esistono), ma va guardato
   * due volte. Da qui esce il valore punto che decide i punti di SL e TP.
   */
  const avvisi: SymbolSpecWarning[] = symbolSpecWarnings(
    {
      canonical,
      digits,
      point,
      valuePerPoint,
      lotStep,
      minLot,
      maxLot: symbol?.maxLot,
    },
    { serverKind: server.kind, peers: peersOf(server.id, canonical.trim().toUpperCase()) },
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: SymbolInput = {
      canonical: canonical.trim().toUpperCase(),
      brokerSymbol: brokerSymbol.trim(),
      digits: intInput(digits) ?? NaN,
      point: decimalInput(point) ?? "",
      valuePerPoint: decimalInput(valuePerPoint) ?? "",
      lotStep: decimalInput(lotStep) ?? "",
      minLot: decimalInput(minLot) ?? "",
      stopsLevel: intInput(stopsLevel) ?? NaN,
    };
    const invalid = [
      Number.isNaN(body.digits) && "digits",
      !body.point && "point",
      !body.valuePerPoint && "valore punto",
      !body.lotStep && "lot step",
      !body.minLot && "lotto minimo",
      Number.isNaN(body.stopsLevel) && "stops level",
    ].filter(Boolean);
    if (invalid.length) return setError(`Controlla: ${invalid.join(", ")}. Usa numeri, per esempio 0.01.`);
    if (Number(body.valuePerPoint) <= 0) return setError("Il valore punto deve essere maggiore di zero");

    setBusy(true);
    try {
      if (symbol) await api.servers.updateSymbol(server.id, symbol.id, body);
      else await api.servers.addSymbol(server.id, body);
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const numField = (label: string, value: string, set: (v: string) => void, hint?: string, placeholder?: string) => (
    <Field label={label} hint={hint}>
      {(id) => (
        <input id={id} required inputMode="decimal" value={value} placeholder={placeholder} onChange={(e) => set(e.target.value)} className={inputClass} />
      )}
    </Field>
  );

  return (
    <SidePanel
      open
      onClose={onClose}
      title={symbol ? `Modifica ${symbol.canonical}` : "Simbolo a mano"}
      subtitle={`${server.displayName ?? server.name} · valori scritti a mano: restano NON verificati`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="symbol-form" disabled={busy}>
            {busy ? "Salvataggio…" : symbol ? "Salva" : "Aggiungi simbolo"}
          </Button>
        </>
      }
    >
      <form id="symbol-form" onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Simbolo canonico" hint="Uguale su tutti i server, per esempio XAUUSD.">
            {(id) => <input id={id} required value={canonical} onChange={(e) => setCanonical(e.target.value.toUpperCase())} className={inputClass} />}
          </Field>
          <Field label="Simbolo sul server" hint="Com'è scritto qui, per esempio XAUUSD-E.">
            {(id) => <input id={id} required value={brokerSymbol} onChange={(e) => setBrokerSymbol(e.target.value)} className={inputClass} />}
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {numField("Digits", digits, setDigits, undefined, "2")}
          {numField("Point", point, setPoint, "Valore di un punto in prezzo.", "0.01")}
        </div>
        {numField(
          "Valore punto",
          valuePerPoint,
          setValuePerPoint,
          "Valore di 1 punto su 1 lotto, nella valuta del conto. Leggilo dal terminale: vale 1 su XAUUSD, non su altri simboli, e un valore sbagliato sbaglia tutti i lotti.",
        )}
        <div className="grid grid-cols-3 gap-3">
          {numField("Lot step", lotStep, setLotStep, undefined, "0.01")}
          {numField("Lotto minimo", minLot, setMinLot, undefined, "0.01")}
          {numField("Stops level", stopsLevel, setStopsLevel, undefined, "0")}
        </div>
        <p className="text-xs text-faint">
          Il lot step decide l'arrotondamento per difetto del lotto su questo server: le due gambe usano ciascuna il proprio.
        </p>
        {avvisi.length > 0 && (
          <div role="status" className="space-y-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            {avvisi.map((a) => (
              <p key={a.code}>{a.message}</p>
            ))}
            <p className="text-xs text-warn/80">
              Si salva lo stesso: è un controllo di plausibilità, non una regola. Ma se il numero viene dal terminale, ricontrollalo lì.
            </p>
          </div>
        )}
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}

function ServerPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const firms = usePropFirms();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"PROP" | "BROKER">("PROP");
  const [timezone, setTimezone] = useState("Etc/GMT-3");
  const [firmId, setFirmId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setKind("PROP");
      setTimezone("Etc/GMT-3");
      setFirmId("");
      setError(null);
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.servers.create({ name: name.trim(), kind, timezone: timezone.trim(), propFirmId: kind === "PROP" && firmId ? firmId : null });
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Nuovo server"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="server-form" disabled={busy}>
            {busy ? "Salvataggio…" : "Crea server"}
          </Button>
        </>
      }
    >
      <form id="server-form" onSubmit={submit} className="space-y-4">
        <Field label="Tipo">
          {(id) => (
            <div id={id} role="radiogroup" className="grid grid-cols-2 gap-2">
              {(["PROP", "BROKER"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  onClick={() => setKind(k)}
                  className={[
                    "rounded border px-2 py-1.5 text-sm",
                    kind === k ? (k === "PROP" ? "border-prop bg-prop/15 text-prop" : "border-broker bg-broker/15 text-broker") : "border-line text-muted hover:text-ink",
                  ].join(" ")}
                >
                  {k === "PROP" ? "Prop" : "Broker"}
                </button>
              ))}
            </div>
          )}
        </Field>
        <Field label="Nome esatto del server MT5" hint="Come compare nel terminale, per esempio FundedNext-Server 2.">
          {(id) => <input id={id} required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Fuso del server" hint="Nome IANA, per esempio Etc/GMT-3. Decide la mezzanotte MT5 e i reset giornalieri.">
          {(id) => <input id={id} required value={timezone} onChange={(e) => setTimezone(e.target.value)} className={inputClass} />}
        </Field>
        {kind === "PROP" && (
          <Field label="Prop firm">
            {(id) => (
              <select id={id} value={firmId} onChange={(e) => setFirmId(e.target.value)} className={inputClass}>
                <option value="">Non indicata</option>
                {(firms.data ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}
